import React from 'react';
import { Alert, Box, Button, ButtonBase, Chip, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { SUBMISSION_LIMITS, SubmissionType } from '../../../../model/ser/ser-enums';
import { formatDate } from '../../../../utils/date';
import { formatFileSize } from '../../../../utils/format';
import { DraftStatus, SubmissionDraft, getDraftStatus, totalFilesSize } from '../submission-send-rules';

const STATUS_CHIP: Record<DraftStatus, { label: string; color: 'success' | 'warning' | 'error' }> = {
    ready: { label: 'Pronta', color: 'success' },
    pending: { label: 'Pendente', color: 'warning' },
    error: { label: 'Erro', color: 'error' },
};

function plural(count: number, singular: string, pluralForm: string) {
    return `${count} ${count === 1 ? singular : pluralForm}`;
}

function DraftCard({ draft, index, active, disabled, onSelect, onDuplicate, onRemove }: {
    draft: SubmissionDraft; index: number; active: boolean; disabled: boolean;
    onSelect: () => void; onDuplicate: () => void; onRemove: () => void;
}) {
    const status = STATUS_CHIP[getDraftStatus(draft)];
    const details = [
        draft.publishDate ? formatDate(draft.publishDate) : 'Sem data',
        plural(draft.files.length, 'arquivo', 'arquivos'),
        plural(draft.vehicleIds.length, 'veículo', 'veículos'),
    ].join(' · ');

    return (
        <Box
            sx={(theme) => ({
                position: 'relative', borderRadius: 2, border: '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
                bgcolor: active ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                boxShadow: active ? `inset 3px 0 0 ${theme.palette.primary.main}` : 'none',
                transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
            })}
        >
            <ButtonBase onClick={onSelect} disabled={disabled} aria-current={active} sx={{ width: '100%', textAlign: 'left', p: 1.5, pr: 9, borderRadius: 2, display: 'block' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>#{index + 1}</Typography>
                    <Chip size="small" color={status.color} variant={active ? 'filled' : 'outlined'} label={status.label} sx={{ height: 20, fontSize: 11 }} />
                    {draft.type === SubmissionType.Urgencia && <PriorityHighIcon sx={{ fontSize: 16 }} color="warning" titleAccess="Urgência" />}
                </Box>
                <Typography variant="body2" fontWeight={700} noWrap color={draft.title.trim() ? 'text.primary' : 'text.secondary'}>
                    {draft.title.trim() || 'Remessa sem título'}
                </Typography>
                <Typography variant="caption" color="text.secondary">{details}</Typography>
                {draft.apiErrors.length > 0 && (
                    <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>{draft.apiErrors[0]}</Typography>
                )}
            </ButtonBase>
            <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex' }}>
                <Tooltip title="Duplicar">
                    <span><IconButton size="small" disabled={disabled} aria-label="Duplicar remessa" onClick={onDuplicate}><ContentCopyIcon fontSize="small" /></IconButton></span>
                </Tooltip>
                <Tooltip title="Remover">
                    <span><IconButton size="small" disabled={disabled} aria-label="Remover remessa" onClick={onRemove}><DeleteOutlineIcon fontSize="small" /></IconButton></span>
                </Tooltip>
            </Box>
        </Box>
    );
}

interface DraftListProps {
    drafts: SubmissionDraft[];
    activeKey: string;
    sending: boolean;
    progress: number;
    generalErrors: string[];
    onSelect: (key: string) => void;
    onDuplicate: (key: string) => void;
    onRemove: (draft: SubmissionDraft) => void;
    onAdd: () => void;
    onSend: () => void;
}

// Painel lateral: remessas montadas neste envio (lote), totais e botão de envio
export function DraftList({ drafts, activeKey, sending, progress, generalErrors, onSelect, onDuplicate, onRemove, onAdd, onSend }: DraftListProps) {
    const totalSize = totalFilesSize(drafts);
    const totalFiles = drafts.reduce((sum, d) => sum + d.files.length, 0);
    const overRequestLimit = totalSize > SUBMISSION_LIMITS.maxRequestSizeMb * 1024 * 1024;
    const pending = drafts.filter((d) => getDraftStatus(d) !== 'ready').length;
    const full = drafts.length >= SUBMISSION_LIMITS.maxSubmissionsPerBatch;

    return (
        <Paper sx={{ p: 2.5, position: { lg: 'sticky' }, top: { lg: 88 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="subtitle1" fontWeight={700}>Remessas deste envio</Typography>
                <Typography variant="caption" color="text.secondary">{drafts.length}/{SUBMISSION_LIMITS.maxSubmissionsPerBatch}</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: { lg: 'calc(100vh - 460px)' }, minHeight: { lg: 96 }, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
                {drafts.map((draft, index) => (
                    <DraftCard
                        key={draft.key}
                        draft={draft}
                        index={index}
                        active={draft.key === activeKey}
                        disabled={sending}
                        onSelect={() => onSelect(draft.key)}
                        onDuplicate={() => onDuplicate(draft.key)}
                        onRemove={() => onRemove(draft)}
                    />
                ))}
            </Box>

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAdd}
                disabled={sending || full}
                sx={{ borderStyle: 'dashed', '&:hover': { borderStyle: 'dashed' } }}
            >
                Adicionar outra remessa
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary' }}>
                <Typography variant="body2">{plural(totalFiles, 'arquivo', 'arquivos')}</Typography>
                <Typography variant="body2" fontWeight={600} color={overRequestLimit ? 'error.main' : 'text.secondary'}>
                    {formatFileSize(totalSize)} de {SUBMISSION_LIMITS.maxRequestSizeMb} MB
                </Typography>
            </Box>

            {overRequestLimit && (
                <Alert severity="warning">O total passa de {SUBMISSION_LIMITS.maxRequestSizeMb} MB e pode ser recusado. Divida as remessas em mais de um envio.</Alert>
            )}
            {generalErrors.length > 0 && (
                <Alert severity="error">{generalErrors.map((message) => <div key={message}>{message}</div>)}</Alert>
            )}

            {sending && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{progress < 100 ? 'Enviando arquivos…' : 'Registrando remessas…'}</Typography>
                        <Typography variant="caption" fontWeight={700}>{progress}%</Typography>
                    </Box>
                    <LinearProgress variant={progress < 100 ? 'determinate' : 'indeterminate'} value={progress} sx={{ height: 6, borderRadius: 3 }} />
                </Box>
            )}

            <LoadingButton
                variant="contained"
                size="large"
                startIcon={<SendIcon />}
                loadingPosition="start"
                loading={sending}
                onClick={onSend}
                sx={{ py: 1.25, fontWeight: 700 }}
            >
                {drafts.length === 1 ? 'Enviar remessa' : `Enviar ${drafts.length} remessas`}
            </LoadingButton>
            {pending > 0 && !sending && (
                <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: -1 }}>
                    {pending === 1 ? '1 remessa pendente' : `${pending} remessas pendentes`}. Complete antes de enviar.
                </Typography>
            )}
        </Paper>
    );
}
